import React from 'react';
import { Link } from 'gatsby';
import LazyLoad from 'react-lazy-load';
import { motion, AnimatePresence } from 'framer-motion';
import { getLinkPath } from '../../utils/helpers';

// hooks
import { useSwimlane } from './useSwimlane/useSwimlane';

// Models
import { IMedia } from '../../utils/models';

// Components
import Poster from '../Poster';
import { ChevronCircleIcon } from '../Icons';

import './Swimlane.scss';

export interface ISwimlaneProps {
  title: string;
  titleIcon?: string;
  titleButton?: {
    text: string;
    href?: string;
    action?: () => void;
  };
  items: IMedia[];
  analyticsConfig: {
    analytics: any;
    eventCase: string;
  };
}

import initTealiumTracker from 'tealium-tracker';
import { KiteIcon } from '@kite/react-kite';
import { ButtonLink } from '../common';
let tealiumTrackEvent;
if (typeof window !== 'undefined') {
  const { trackEvent } = initTealiumTracker({ debugMode: true });
  tealiumTrackEvent = trackEvent;
}

const allowsAssetTypes = new Set(['Series', 'Movie']);

const Swimlane = ({
  title = '',
  titleIcon,
  titleButton,
  items,
  analyticsConfig: { eventCase, analytics },
}: ISwimlaneProps) => {
  const {
    scrollContainerRef,
    showPreviousButton,
    showNextButton,
    handleButtonClick,
    contentLoaded,
    setContentLoaded,
  } = useSwimlane();

  // prevent swimlane from rendering if empty
  if (!items || !items?.length) {
    return null;
  }

  const generateTitleId = () => {
    const splitTitle = title.toLowerCase().split(' ');
    return splitTitle.reduce((id, word, i) => {
      const idString = word.replace(/espaã±ol/g, 'espanol');
      if (
        idString === '-' ||
        splitTitle[i + 1] === '-' ||
        i + 1 === splitTitle.length
      ) {
        id += idString;
      } else if (idString === '&') {
        id += 'and-';
      } else {
        id += `${idString}-`;
      }
      return id;
    }, '');
  };

  const swimlaneItems = items.map((item, i) => {
    // Only Show Movie and Series Types
    if (!allowsAssetTypes.has(item?.assetType)) {
      return null;
    }

    // Filter out null offers since pages have not been created for them
    if (!item?.offer?.length && !item.isAccess) {
      return null;
    }

    const scrollContainerWidth = scrollContainerRef?.current?.offsetWidth;

    return (
      <LazyLoad
        key={i}
        elementType="li"
        width={250}
        offsetVertical={500}
        offsetHorizontal={scrollContainerWidth || 500}
        throttle={250}
        debounce={false}
        onContentVisible={() => !contentLoaded && setContentLoaded(true)}
        className="swimlane__slider-item"
      >
        <Link
          aria-label={`${title}`}
          to={item.path || getLinkPath(item)}
          onClick={() => {
            analytics.track(eventCase, {
              tmsProgramId: item.altIds.tmsRootId,
              msgCategory: 'navigation',
              currPageSubsecName: title,
              currPageElemUiName: item.title,
              currPageElemIndex: i,
              opType: 'assetSelection',
            });
            tealiumTrackEvent({
              linktype: 'swimlane-asset',
              linkname: title,
            });
          }}
          data-linktype="swimlane-asset"
          data-linkname={title}
        >
          <Poster media={item} />
        </Link>
      </LazyLoad>
    );
  });

  let titleRow = (
    <h2 className="swimlane__title">
      {/* Revert once API is fixed */}
      {title.replace(/EspaÃ±ol/, 'Español')}
      {titleIcon && <KiteIcon icon={titleIcon} size="2.25rem" />}
    </h2>
  );

  if (titleButton) {
    titleRow = (
      <div className="swimlane__title-row">
        <h2 className="swimlane__title">
          {/* Revert once API is fixed */}
          {/* <span>{title.replace(/EspaÃ±ol/, 'Español')}</span> */}
          {title.replace(/EspaÃ±ol/, 'Español')}
          {titleIcon && <KiteIcon icon={titleIcon} size="2.25rem" />}
        </h2>
        <span className="swimlane__title-button-container">
          <ButtonLink
            className="swimlane__title-button"
            type="transparent"
            path={titleButton.href || '#'}
            onClick={titleButton.action}
            target="_blank"
          >
            {titleButton.text}
          </ButtonLink>
        </span>
      </div>
    );
  }

  return (
    <section className="swimlane" id={generateTitleId()}>
      {titleRow}
      <div className="swimlane__slider-container">
        <AnimatePresence>
          {showPreviousButton && (
            <motion.button
              initial={{ opacity: 0, x: -16, y: '-75%' }}
              animate={{ opacity: 1, x: -8, y: '-75%' }}
              exit={{ opacity: 0, x: -16, y: '-75%' }}
              transition={{ duration: 0.3, type: 'tween' }}
              onClick={() => handleButtonClick('previous')}
              className="swimlane__button"
              tabIndex={-1}
              aria-hidden={true}
              title="Previous"
            >
              <ChevronCircleIcon direction="left" />
            </motion.button>
          )}
        </AnimatePresence>
        <div className="swimlane__slider-container-crop">
          <ul
            ref={scrollContainerRef}
            className="swimlane__slider"
            aria-label={title}
          >
            {swimlaneItems}
          </ul>
        </div>
        <AnimatePresence>
          {showNextButton && (
            <motion.button
              initial={{ opacity: 0, x: 16, y: '-75%' }}
              animate={{ opacity: 1, x: 8, y: '-75%' }}
              exit={{ opacity: 0, x: 16, y: '-75%' }}
              transition={{ duration: 0.3, type: 'tween' }}
              onClick={() => handleButtonClick('next')}
              className="swimlane__button"
              tabIndex={-1}
              aria-hidden={true}
              title="Next"
            >
              <ChevronCircleIcon direction="right" />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Swimlane;
